import React, { useContext, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { FiArrowRight, FiLayers, FiTrendingUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import { UserContext } from '../App';
import { db } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './CreatorProducts.module.css';
import { getRoleAccessState, ROLE_ARTIST } from '../utils/roles';
import { formatDate, formatDropStatus, formatLocalDateTime, getDropStatus } from '../utils/marketplace';
import { getCompactCollectibleImagePath } from '../utils/collectibleMedia';

const PAGE_SIZE = 12;

export default function CreatorProducts() {
  const appUser = useContext(UserContext);
  const artistAccess = getRoleAccessState(appUser, ROLE_ARTIST);
  const navigate = useNavigate();
  const [drops, setDrops] = useState([]);
  const [metrics, setMetrics] = useState({
    productCount: 0,
    totalSold: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!appUser?.id) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [dashboardSnap, dropsSnap] = await Promise.all([
          getDoc(doc(db, 'artist_dashboards', appUser.id)),
          getDocs(query(collection(db, 'drops'), where('artistOwnerUid', '==', appUser.id))),
        ]);
        if (!active) return;
        const nextDrops = dropsSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }))
          .sort((left, right) => Number(right.updatedAt?.seconds || 0) - Number(left.updatedAt?.seconds || 0));
        const productIds = [...new Set(nextDrops.map((drop) => String(drop.productId || '').trim()).filter(Boolean))];
        const productDocs = await Promise.all(productIds.map(async (productId) => {
          const snap = await getDoc(doc(db, 'products', productId));
          return snap.exists() ? [productId, snap.data() || {}] : [productId, null];
        }));
        const productMap = new Map(productDocs);
        setDrops(nextDrops.map((drop) => ({
          ...drop,
          uiSoldCount: Number(productMap.get(String(drop.productId || '').trim())?.inventory?.soldCount || productMap.get(String(drop.productId || '').trim())?.soldCount || 0),
        })));
        const summary = dashboardSnap.exists() ? dashboardSnap.data()?.summary || {} : {};
        setMetrics({
          productCount: Number(summary.productCount || 0),
          totalSold: Number(summary.totalSold || 0),
        });
        setError('');
      } catch (err) {
        if (!active) return;
        setDrops([]);
        setError(err?.message || 'Unable to load drops.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [appUser?.id]);

  useEffect(() => { setPage(1); }, [drops.length]);

  const totalPages = Math.max(1, Math.ceil(drops.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleDrops = useMemo(() => drops.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE), [currentPage, drops]);

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/creator')} />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <header className={styles.header}>
          <div>
            <span className={styles.eyebrow}>Creator Products</span>
            <h1>Drops</h1>
            <p>Create and manage single drops now. Optional collections can group drops later without changing this workflow.</p>
          </div>
          <button type="button" className={styles.primaryButton} onClick={() => navigate('/creator/drops/new')} disabled={!artistAccess.isActive}>Create Drop</button>
        </header>

        {!artistAccess.isActive ? <p className={styles.state}>Artist access is expired. Drop creation stays disabled until you reactivate.</p> : null}
        {error ? <p className={styles.error}>{error}</p> : null}

        <section className={styles.metricsGrid}>
          <article className={styles.metricCard}><div className={styles.metricIcon}><FiLayers /></div><span>Total Products</span><strong>{loading ? '…' : metrics.productCount}</strong></article>
          <article className={styles.metricCard}><div className={styles.metricIcon}><FiTrendingUp /></div><span>Total Sold</span><strong>{loading ? '…' : metrics.totalSold}</strong></article>
        </section>

        <section className={styles.catalogCard}>
          <div className={styles.sectionHeader}>
            <div>
              <h2>All Drops</h2>
              <p>{loading ? 'Loading drops…' : `${drops.length} drop${drops.length === 1 ? '' : 's'} total`}</p>
            </div>
            <span className={styles.pageMeta}>Page {currentPage} of {totalPages}</span>
          </div>

          {loading ? <p className={styles.state}>Loading drops…</p> : null}
          {!loading && !visibleDrops.length ? <p className={styles.state}>No drops created yet.</p> : null}

          <div className={styles.list}>
            {visibleDrops.map((drop) => (
              <button
                key={drop.id}
                type="button"
                className={styles.listItem}
                onClick={() => navigate(String(drop.status || '') === 'draft' ? `/creator/drops/new?draft=${drop.id}` : `/creator/drops/${drop.id}`)}
              >
                <div className={styles.listTopRow}>
                  <div className={styles.thumbWrap}>
                    <ResolvedStorageImage
                      path={getCompactCollectibleImagePath({ media: drop.media || [] })}
                      alt={drop.title || 'Drop'}
                      className={styles.thumb}
                      fallback={<div className={styles.thumbPlaceholder}>Drop</div>}
                    />
                  </div>
                  <div className={styles.listHeader}>
                    <div className={styles.listHeaderCopy}>
                      <strong>{drop.title || drop.id}</strong>
                      <span className={styles.statusPill}>{formatDropStatus(drop, nowMs)}</span>
                    </div>
                    <FiArrowRight className={styles.listItemIcon} />
                  </div>
                </div>
                <div className={styles.listCopy}>
                  <p>{drop.description || 'No description added yet.'}</p>
                  <div className={styles.statsGrid}>
                    <span>Status <strong>{formatDropStatus(drop, nowMs)}</strong></span>
                    <span>Price <strong>{drop.priceAmount || 0} {drop.currency || 'USD'}</strong></span>
                    <span>Sold <strong>{Number(drop.uiSoldCount || 0)}</strong></span>
                    <span>Starts <strong>{drop.saleStartAt ? formatLocalDateTime(drop.saleStartAt) : 'Draft'}</strong></span>
                    <span>Ends <strong>{drop.saleEndAt ? formatDate(drop.saleEndAt) : 'Draft'}</strong></span>
                    <span>Action <strong>{String(drop.status || '') === 'draft' ? 'Resume draft' : getDropStatus(drop, nowMs) === 'canceled' ? 'Closed' : 'Open detail'}</strong></span>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {drops.length > PAGE_SIZE ? (
            <div className={styles.pagination}>
              <button type="button" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</button>
              <span>{currentPage} / {totalPages}</span>
              <button type="button" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</button>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
